import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { DisclaimerFooter } from '@/components/DisclaimerFooter';

// Reuses the visual language of the landing page — Playfair serif, warm-neutral palette,
// glass cards on the dark section. No urgency copy, no scoring/points language.

const HeroPreview = ({ onCta }) => (
  <section
    className="relative pt-24 pb-16 px-4 overflow-hidden"
    style={{ background: 'linear-gradient(180deg, #F8F5F0 0%, #EDE8E0 100%)' }}
  >
    <div className="max-w-3xl mx-auto text-center">
      <div className="flex justify-center mb-8">
        <div
          className="w-24 h-24 rounded-full opacity-70"
          style={{
            background:
              'radial-gradient(circle, rgba(201,168,76,0.55) 0%, rgba(232,180,184,0.28) 55%, transparent 100%)',
            boxShadow: '0 0 70px rgba(201,168,76,0.35)',
            animation: 'iw-pulse 4s ease-in-out infinite',
          }}
        />
      </div>
      <p
        className="text-sm md:text-base tracking-[0.2em] uppercase mb-6"
        style={{ color: '#a89968' }}
        data-testid="iw-preview-eyebrow"
      >
        Inside Sage
      </p>
      <h1
        className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight mb-6 leading-tight"
        style={{ fontFamily: 'Playfair Display, serif', color: '#2C2C2C' }}
        data-testid="iw-preview-headline"
      >
        The <span className="italic" style={{ color: '#C9A84C' }}>Inner World</span>
      </h1>
      <p
        className="text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto"
        style={{ fontFamily: 'Playfair Display, serif', color: '#5a5248', fontStyle: 'italic' }}
        data-testid="iw-preview-subhead"
      >
        A place inside Sage for the parts of you that don't come out in words right away.
      </p>
    </div>
    <style>{`
      @keyframes iw-pulse {
        0%, 100% { transform: scale(1); opacity: 0.7; }
        50% { transform: scale(1.05); opacity: 0.9; }
      }
    `}</style>
  </section>
);

const ProseSection = ({ eyebrow, children, background, textColor, eyebrowColor, testid }) => (
  <section
    className="relative py-20 md:py-24 px-4"
    style={{ background }}
    data-testid={testid}
  >
    <div className="max-w-2xl mx-auto">
      <p
        className="text-xs md:text-sm tracking-[0.22em] uppercase mb-8"
        style={{ color: eyebrowColor }}
      >
        {eyebrow}
      </p>
      <div
        className="space-y-6 text-lg md:text-xl leading-relaxed"
        style={{ color: textColor, fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
      >
        {children}
      </div>
    </div>
  </section>
);

const WhatsInsideSection = () => {
  const items = [
    {
      title: 'A world that remembers you',
      body:
        'Settings shaped by your own words, not a fixed map everyone walks the same way.',
    },
    {
      title: 'Companions, not challenges',
      body:
        'The parts of you that show up here become part of your story, not something to defeat.',
    },
    {
      title: 'A keepsake of your own making',
      body:
        'Moments you choose to keep, gathered into something you can look back on.',
    },
    {
      title: 'A place to rest',
      body: 'One tap, any time, back to stillness. No explanation required.',
    },
  ];

  return (
    <section
      className="relative py-20 md:py-24 px-4"
      style={{ backgroundColor: '#FBF9F6' }}
      data-testid="iw-preview-inside"
    >
      <div className="max-w-4xl mx-auto">
        <p
          className="text-xs md:text-sm tracking-[0.22em] uppercase mb-10 text-center"
          style={{ color: 'rgba(201,168,76,0.85)' }}
        >
          What's Inside
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item) => (
            <div
              key={item.title}
              className="rounded-2xl p-8 md:p-10"
              style={{
                background: '#F9F4F0',
                border: '1px solid rgba(201,168,76,0.18)',
                backdropFilter: 'blur(14px)',
              }}
              data-testid={`iw-inside-${item.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <h3
                className="text-2xl font-normal mb-4 leading-snug"
                style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#1A1A2E',
                }}
              >
                {item.title}
              </h3>
              <p
                className="text-base md:text-lg leading-relaxed"
                style={{ color: '#4A4A5A' }}
              >
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BoundarySection = () => (
  <section
    className="relative py-16 md:py-20 px-4"
    style={{ background: 'linear-gradient(180deg, #EDE8E0 0%, #E4DDD1 100%)' }}
    data-testid="iw-preview-boundary"
  >
    <div className="max-w-2xl mx-auto">
      <div
        className="rounded-2xl p-8 md:p-10"
        style={{
          background: '#5C5968',
          border: '1px solid rgba(201,168,76,0.28)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <p
          className="text-xs tracking-[0.22em] uppercase mb-5"
          style={{ color: '#a89968' }}
        >
          A gentle note
        </p>
        <p
          className="text-lg md:text-xl leading-relaxed"
          style={{ fontFamily: 'Playfair Display, serif', color: '#3a3630' }}
        >
          This is a space for reflection, not a replacement for care. If something here brings up more than feels manageable alone, we'll always point you toward a licensed therapist — that's true throughout Sage, and it's true here too.
        </p>
      </div>
    </div>
  </section>
);

const CtaSection = ({ onCta }) => (
  <section
    className="relative py-20 md:py-28 px-4 text-center overflow-hidden"
    style={{ backgroundColor: '#FBF9F6' }}
    data-testid="iw-preview-cta"
  >
    <div className="max-w-2xl mx-auto">
      <Button
        onClick={onCta}
        size="lg"
        className="btn-gradient rounded-full px-10 py-6 text-base md:text-lg text-[#FBF9F6] border-0"
        data-testid="iw-preview-cta-btn"
      >
        <Sparkles className="w-5 h-5 mr-2" />
        Begin Your Healing Journey
      </Button>
      <p className="text-sm mt-5" style={{ color: '#6B6878' }}>
        Included with your Sage membership · 14-day free trial · Cancel anytime
      </p>
    </div>
  </section>
);

export default function InnerWorldPreviewPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  useEffect(() => {
    document.title = 'The Inner World — Sage';
  }, []);

  const handleCta = () => {
    if (user && token) {
      navigate('/sanctuary');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen" data-testid="inner-world-preview-page">
      <HeroPreview onCta={handleCta} />

      <ProseSection
        eyebrow="What it is"
        background="#F8F5F0"
        textColor="#3a3630"
        eyebrowColor="#a89968"
        testid="iw-preview-what-it-is"
      >
        <p>
          Some things don't surface in a conversation. They surface in a memory, a feeling in your chest, a reaction that seems bigger than the moment. The Inner World gives those parts of you somewhere to go that isn't rushed and isn't graded.
        </p>
        <p>
          It's not a test. It's not a program you complete. It's a quiet, unhurried space that grows the way you grow — nothing more, nothing less.
        </p>
      </ProseSection>

      <ProseSection
        eyebrow="How it feels"
        background="#EDE8E0"
        textColor="#3a3630"
        eyebrowColor="#a89968"
        testid="iw-preview-how-it-feels"
      >
        <p>
          You'll meet parts of yourself the way you might meet someone new — with curiosity, not judgment. The part that protects. The part that avoids. The part that's been waiting a long time to be heard. Each one gets a place, not a verdict.
        </p>
        <p>
          Nothing here keeps score. Nothing here punishes a day you didn't show up. It stays exactly as you left it, and it's glad to see you when you're back.
        </p>
      </ProseSection>

      <WhatsInsideSection />
      <BoundarySection />
      <CtaSection onCta={handleCta} />
      <DisclaimerFooter />
    </div>
  );
}
